<script setup>
import ProductPriceDetailsTable from './shared/ProductPriceDetailsTable.vue'
import BaseSelectCustomer from '../base/BaseSelectCustomer.vue'
import PricingFilterCard from './shared/PricingFilterCard.vue'
import PricingStatusSelect from './shared/PricingStatusSelect.vue'
import SpecialPricingChipsCell from './shared/SpecialPricingChipsCell.vue'
import PricingCountChip from './shared/PricingCountChip.vue'
import BasePerPageSelect from '../base/BasePerPageSelect.vue'
import BasePagination from '../base/BasePagination.vue'

import { useStore } from '@/utils/VuexHelpers'
import { computed, ref } from 'vue'

const expandedCustomers = ref([])

const store = useStore()
const specialPricingCustomers = computed(
  () => store.getters.specialPricingCustomers
)
const totalSpecialPricingCustomers = computed(
  () => store.getters.totalSpecialPricingCustomers
)
const loadingCust = computed(
  () => store.getters.loadingSpecialPricingCust === 'list'
)
const customerNames = computed(() => store.getters.customerNames)
const perPageSpecialPricingCustomers = computed(
  () => store.getters.perPageSpecialPricingCustomers
)
const pageNrSpecialPricingCustomers = computed(
  () => store.getters.pageNrSpecialPricingCustomers
)
const loadingProducts = computed(
  () => store.getters.loadingProducts === 'names'
)

const customerHeaders = [
  {
    text: '',
    value: 'data-table-expand',
    sortable: false,
    cellClass: 'customer-expand'
  },
  {
    text: 'Customer',
    value: 'company',
    sortable: true,
    cellClass: 'customer-root-cell'
  },
  {
    text: 'Products with Special Pricing',
    value: 'prices',
    align: 'start',
    sortable: false,
    cellClass: 'pt-1'
  },
  {
    text: 'Prices Count',
    value: 'total_count',
    align: 'start',
    sortable: true
  }
]

const perPage = computed({
  get: () => perPageSpecialPricingCustomers.value,
  set: (value) => {
    store.commit('SET_PER_PAGE_SPECIAL_PRICING_CUSTOMERS', value)
    store.commit('SET_PAGE_NR_SPECIAL_PRICING_CUSTOMERS', 0)
    store.dispatch('fetchSpecialPricingCustomers')
  }
})

const updatePageNr = (page) => {
  store.commit('SET_PAGE_NR_SPECIAL_PRICING_CUSTOMERS', page - 1)
  store.dispatch('fetchSpecialPricingCustomers')
}

const pricingStatus = computed({
  get: () => {
    return store.getters.filtersSpecialPricingCustomers.prices.value || ''
  },
  set: (value) => {
    store.commit('SET_FILTER_SPECIAL_PRICING_CUSTOMER', {
      field: 'prices',
      value
    })
    store.commit('SET_PAGE_NR_SPECIAL_PRICING_CUSTOMERS', 0)
    store.dispatch('fetchSpecialPricingCustomers')
  }
})

const selectedCustomerFilter = computed({
  get: () => {
    const code = store.getters.filtersSpecialPricingCustomers.code.value

    if (!code) return null

    const cust = customerNames.value.find((customer) => {
      return String(customer.code) === String(code)
    })

    return cust?.id || null
  },
  set: (customerId) => {
    const customer =
      customerNames.value.find((customer) => {
        return String(customer.id) === String(customerId)
      }) || null

    store.commit('SET_FILTER_SPECIAL_PRICING_CUSTOMER', {
      field: 'code',
      value: customer?.code || ''
    })
    store.commit('SET_PAGE_NR_SPECIAL_PRICING_CUSTOMERS', 0)
    store.dispatch('fetchSpecialPricingCustomers')
  }
})

const isCustomerExpanded = (item) => {
  const isCustExpanded = expandedCustomers.value.some((expandedItem) => {
    return Number(expandedItem.id) === Number(item.id)
  })

  return isCustExpanded
}

const getCustomerRowClass = (item) => {
  return isCustomerExpanded(item) ? 'customer-row-expanded' : ''
}

const refreshPageData = async () => {
  // await store.dispatch('fetchProductNames')
  store.dispatch('fetchSpecialPricingPageData')
}
</script>

<template>
  <v-tab-item value="customer" class="pa-2">
    <PricingFilterCard>
      <v-row dense align="center">
        <v-col cols="12" md="4" lg="3">
          <BaseSelectCustomer
            label="Customer"
            :customer="selectedCustomerFilter"
            @update:customer="selectedCustomerFilter = $event"
            class="customer-select"
          />
        </v-col>

        <v-col cols="12" md="3" lg="2">
          <PricingStatusSelect v-model="pricingStatus" />
        </v-col>

        <v-spacer />

        <v-col cols="12" md="auto">
          <div class="d-flex align-center">
            <BasePerPageSelect
              v-model="perPage"
              class="mr-3"
              :label="'Customers per page'"
            />
            <PricingCountChip
              label="Total customers"
              :count="totalSpecialPricingCustomers"
            />
          </div>
        </v-col>
      </v-row>
    </PricingFilterCard>
    <v-data-table
      :headers="customerHeaders"
      :items="specialPricingCustomers"
      item-key="id"
      show-expand
      height="800"
      fixed-header
      :expanded.sync="expandedCustomers"
      :loading="loadingCust || loadingProducts"
      loading-text="Loading customer product pricing..."
      class="elevation-1 customer-pricing-table"
      :item-class="getCustomerRowClass"
      :server-items-length="totalSpecialPricingCustomers"
      hide-default-footer
    >
      <template #item.company="{ value, item }">
        <v-row no-gutters class="flex-column">
          <div class="font-weight-bold text-no-wrap">
            {{ value?.name || '-' }}
          </div>

          <small class="font-italic"
            ><b class="text-muted">Code:</b
            ><span class="text-overline"> {{ item.code }}</span></small
          ></v-row
        >
      </template>
      <template #item.prices="{ value }">
        <SpecialPricingChipsCell
          :display-type="'product'"
          :prices="value"
          :max-name-length="20"
          :max-visible="6"
        />
      </template>

      <template #item.total_count="{ item }">{{
        item.prices?.length || 0
      }}</template>

      <template #expanded-item="{ item }">
        <td></td>
        <td
          :colspan="customerHeaders.length - 1"
          class="expanded-row-cell blue-left-border pa-4"
        >
          <div class="expanded-row-wrapper">
            <ProductPriceDetailsTable
              :prices="item.prices"
              :customer="item"
              :allow-product-select="true"
              @refresh="refreshPageData"
            />
          </div>
        </td>
      </template>
    </v-data-table>

    <BasePagination
      :total="totalSpecialPricingCustomers"
      :per-page="perPageSpecialPricingCustomers"
      :current-page="pageNrSpecialPricingCustomers + 1"
      item-label="customers"
      :disabled="loadingCust"
      @update:page="updatePageNr"
      :show="true"
    />
  </v-tab-item>
</template>

<style scoped>
:deep(.pricing-filter-card .customer-select) {
  margin-bottom: 0 !important;
}
</style>
